// SYSTEM_ROUTE: /assistant
import { useState } from 'react';
import type { AiCapability, AiResult, Brief } from '@life-os/app-core';
import { BRIEF_REASON_LABEL } from '@life-os/app-core';
import { Button, Chip, CompactRow, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useRouter } from '../app/router.js';
import { useAsync } from '../lib/useAsync.js';
import { appConfig, appFlags } from '../lib/config.js';
import { useConnectivity } from '../lib/useConnectivity.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const REASON_TONE: Record<string, ChipTone> = {
  agenda: 'primary',
  habit_due: 'warn',
  target_at_risk: 'warn',
  capture_triage: 'neutral',
  project_stalled: 'warn',
  problem_open: 'neutral',
  daily_review: 'neutral',
  all_clear: 'primary',
};

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

export function AssistantScreen() {
  const services = useServices();
  const { revision, data } = useStore();
  const { navigate } = useRouter();
  const connectivity = useConnectivity();
  const brief = useAsync<Brief>(() => services.assistant.brief(todayDate()), [revision]);

  const [prompt, setPrompt] = useState('');
  const [capability, setCapability] = useState<AiCapability>('chat');
  const [aiResult, setAiResult] = useState<AiResult | null>(null);
  const [thinking, setThinking] = useState(false);

  const aiCtx = {
    configured: appFlags.ai,
    online: connectivity === 'online',
    remoteAllowedByPolicy: appConfig.ai.remoteEnabled,
  };

  const ask = async () => {
    if (prompt.trim().length === 0) return;
    setThinking(true);
    try {
      // standard_private is AI-eligible, so routing reflects real config state.
      const result = await data.ai.run(capability, [{ role: 'user', content: prompt }], 'standard_private', aiCtx);
      setAiResult(result);
    } finally {
      setThinking(false);
    }
  };

  const b = brief.value;

  return (
    <>
      <ScreenHeader title={<>Assistant</>} subtitle={<>A calm, read-only brief composed from your own data. It suggests where to look — it never acts
            on its own, and nothing here leaves your device.</>} />

      <Pane title="Daily brief" hint={b ? b.headline : 'Reading your day…'}>
        <Stack>
          {!b ? (
            <CompactRow title="Composing your brief…" />
          ) : (
            b.items.map((item, i) => (
              <CompactRow
                key={i}
                title={item.text}
                meta={
                  <>
                    <Chip tone={REASON_TONE[item.reason] ?? 'neutral'}>{BRIEF_REASON_LABEL[item.reason]}</Chip>
                    {item.reason !== 'all_clear' ? (
                      <Button variant="ghost" onClick={() => navigate(item.route)}>
                        Open
                      </Button>
                    ) : null}
                  </>
                }
              />
            ))
          )}
        </Stack>
      </Pane>

      <Pane
        title="Ask the assistant"
        hint={aiCtx.configured ? (aiCtx.online ? 'Hosted AI available' : 'Offline — using on-device assistant') : 'No AI key — using on-device assistant'}
      >
        <Stack>
          <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={capability} onChange={(e) => setCapability(e.target.value as AiCapability)} aria-label="Capability" style={inputStyle}>
              <option value="chat">Chat</option>
              <option value="summarize">Summarize</option>
              <option value="plan">Plan</option>
              <option value="tidy">Tidy</option>
            </select>
            <input
              className="lc-cmdbar"
              style={{ flex: '1 1 260px' }}
              value={prompt}
              placeholder="Ask, or paste text to summarize/plan…"
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void ask();
              }}
              aria-label="Prompt"
            />
            <Button variant="primary" onClick={() => void ask()}>
              {thinking ? 'Thinking…' : 'Ask'}
            </Button>
          </div>
          {aiResult ? (
            <CompactRow
              title={aiResult.text}
              subtitle={aiResult.usedProvider === 'remote' ? 'Answered by hosted AI' : `On-device assistant${aiResult.reason ? ` · ${aiResult.reason.replace(/_/g, ' ')}` : ''}`}
              meta={<Chip tone={aiResult.usedProvider === 'remote' ? 'primary' : 'neutral'}>{aiResult.usedProvider}</Chip>}
            />
          ) : null}
        </Stack>
      </Pane>

      <Pane title="How this works" hint="Security posture">
        <Stack>
          <CompactRow title="Read-only" subtitle="The brief reads your state and suggests. It never changes data or acts for you." />
          <CompactRow title="Local & private" subtitle="The brief is composed on-device. The Ask box uses hosted AI only for AI-eligible content when you've configured a key; sensitive data always stays on-device." />
          <CompactRow title="Source-linked" subtitle="Every item points to the real system where you can decide and act." />
        </Stack>
      </Pane>
    </>
  );
}
